import { outcomeMarketCapAtPriceD6 } from '@morfi/market-math';

export const LIVE_CANDLE_BUCKET_SECONDS = 5;

export interface LiveCandleTrade {
  readonly eventId: string;
  readonly outcomeIndex: number;
  readonly timestamp?: string | null;
  readonly priceAfter?: string | null;
  readonly usdcAmount?: string | null;
  readonly liquidityModel?: 'bonding_curve' | 'cp_swap' | null;
}

export interface LiveTradeCandleArgs {
  readonly trades: readonly LiveCandleTrade[];
  readonly cutoffSec: number;
  readonly nowSec: number;
  readonly mode: 'price' | 'marketCap';
  readonly bucketSeconds?: number;
  readonly restrictedOutcomeIndex?: number;
  readonly indexedOutcomeMintSupplyD6ByOutcome?: ReadonlyMap<number, string | null | undefined>;
  readonly initialOutcomeSupplyD6ByOutcome?: ReadonlyMap<number, string | null | undefined>;
  readonly virtualQuoteD6ByOutcome?: ReadonlyMap<number, string | null | undefined>;
}

/** OHLC stays D9 for Price and D6 for Market Cap; quote volume is always D6. */
export interface LiveTradeCandle {
  readonly time: number;
  readonly open: string;
  readonly high: string;
  readonly low: string;
  readonly close: string;
  readonly volumeD6?: string;
  readonly tradeCount: number;
  readonly provenance: 'authoritative' | 'closed-gap';
}

/**
 * Rebuild from the authoritative tape so replay and late delivery cannot double
 * count volume. The entire left-edge bucket survives while any part is visible.
 * Like Live lines, these are execution prices; historical charts use post-spot.
 */
export function aggregateLiveTradeCandles(
  args: LiveTradeCandleArgs,
): Record<number, LiveTradeCandle[]> {
  const width = args.bucketSeconds ?? LIVE_CANDLE_BUCKET_SECONDS;
  if (
    !Number.isSafeInteger(width) ||
    width <= 0 ||
    !Number.isFinite(args.cutoffSec) ||
    !Number.isFinite(args.nowSec) ||
    args.cutoffSec > args.nowSec
  )
    return {};
  const firstBucket = Math.floor(args.cutoffSec / width) * width;
  const lastBucket = Math.floor(args.nowSec / width) * width;
  const seen = new Set<string>();
  const buckets = new Map<number, Map<number, LiveTradeCandle>>();
  const predecessors = new Map<number, string>();

  // The API's fixed-width hex event ID encodes (slot, transaction index, CPI).
  // Timestamp assigns a bucket; canonical chain order decides open and close.
  for (const trade of [...args.trades].sort((a, b) =>
    a.eventId < b.eventId ? -1 : a.eventId > b.eventId ? 1 : 0,
  )) {
    if (seen.has(trade.eventId)) continue;
    seen.add(trade.eventId);
    const index = trade.outcomeIndex;
    if (args.restrictedOutcomeIndex !== undefined && index !== args.restrictedOutcomeIndex)
      continue;
    const time = Date.parse(trade.timestamp ?? '') / 1000;
    if (!Number.isFinite(time) || time > args.nowSec) continue;
    const value = candleValue(trade, args);
    if (value === undefined) continue;
    const bucket = Math.floor(time / width) * width;
    if (bucket < firstBucket) {
      predecessors.set(index, value.toString());
      continue;
    }
    const outcomeBuckets = buckets.get(index) ?? new Map<number, LiveTradeCandle>();
    buckets.set(index, outcomeBuckets);
    const prior = outcomeBuckets.get(bucket);
    const volume = unsignedInteger(trade.usdcAmount);
    const volumeD6 =
      prior === undefined
        ? volume
        : prior.volumeD6 === undefined || volume === undefined
          ? undefined
          : BigInt(prior.volumeD6) + volume;
    outcomeBuckets.set(bucket, {
      time: bucket,
      open: prior?.open ?? value.toString(),
      high: prior !== undefined && BigInt(prior.high) > value ? prior.high : value.toString(),
      low: prior !== undefined && BigInt(prior.low) < value ? prior.low : value.toString(),
      close: value.toString(),
      ...(volumeD6 === undefined ? {} : { volumeD6: volumeD6.toString() }),
      tradeCount: (prior?.tradeCount ?? 0) + 1,
      provenance: 'authoritative',
    });
  }

  const result: Record<number, LiveTradeCandle[]> = {};
  for (const index of new Set([...buckets.keys(), ...predecessors.keys()])) {
    let carry = predecessors.get(index);
    const candles: LiveTradeCandle[] = [];
    for (let time = firstBucket; time <= lastBucket; time += width) {
      const candle = buckets.get(index)?.get(time);
      if (candle !== undefined) {
        candles.push(candle);
        carry = candle.close;
      } else if (carry !== undefined && time + width <= args.nowSec) {
        candles.push({
          time,
          open: carry,
          high: carry,
          low: carry,
          close: carry,
          volumeD6: '0',
          tradeCount: 0,
          provenance: 'closed-gap',
        });
      }
    }
    if (candles.length > 0) result[index] = candles;
  }
  return result;
}

function candleValue(trade: LiveCandleTrade, args: LiveTradeCandleArgs): bigint | undefined {
  const priceD9 = unsignedInteger(trade.priceAfter);
  if (priceD9 === undefined || priceD9 <= 0n || trade.liquidityModel == null) return undefined;
  if (args.mode === 'price') return priceD9;
  const index = trade.outcomeIndex;
  const supply = unsignedInteger(args.indexedOutcomeMintSupplyD6ByOutcome?.get(index));
  const initialSupply = unsignedInteger(args.initialOutcomeSupplyD6ByOutcome?.get(index));
  const virtualQuote = unsignedInteger(args.virtualQuoteD6ByOutcome?.get(index));
  if (trade.liquidityModel === 'cp_swap') {
    if (supply === undefined) return undefined;
    const result = outcomeMarketCapAtPriceD6({
      poolModel: 'cp_swap',
      priceD9,
      indexedOutcomeMintSupplyD6: supply,
    });
    return result.available ? result.value : undefined;
  }
  if (initialSupply === undefined || virtualQuote === undefined) return undefined;
  const result = outcomeMarketCapAtPriceD6({
    poolModel: 'bonding_curve',
    priceD9,
    initialOutcomeSupplyD6: initialSupply,
    virtualQuoteReserveD6: virtualQuote,
  });
  return result.available ? result.value : undefined;
}

function unsignedInteger(raw: string | null | undefined): bigint | undefined {
  return raw !== undefined && raw !== null && /^\d+$/.test(raw) ? BigInt(raw) : undefined;
}
